<template>
  <div
    class="custom-image relative overflow-hidden inline-block"
    :class="[roundedClass, { 'has-aspect-ratio': aspectRatio, 'fluid-width': fluidWidth }]"
    :style="aspectRatioStyle"
  >
    <img
      v-if="!hasError"
      :src="src"
      :alt="alt"
      loading="lazy"
      decoding="async"
      class="custom-image-inner"
      :class="objectFit === 'cover' ? 'object-cover' : 'object-contain'"
      @error="onError"
    />
    <slot v-else name="fallback">
      <div
        class="center-xy custom-image-fallback bg-color-medium color-medium w-full h-full min-w-[1.5em] min-h-[1.5em] text-[1.5rem]"
      >
        <i :class="fallbackIcon" aria-hidden="true" />
      </div>
    </slot>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue";

type RoundedKey = "none" | "default" | "full";
type ObjectFitKey = "cover" | "contain";

const ROUNDED_CLASS_MAP: Record<RoundedKey, string> = {
  none: "rounded-none",
  default: "rounded-md",
  full: "rounded-full",
};

export default defineComponent({
  name: "CustomImage",
  props: {
    src: { type: String, required: true },
    alt: { type: String, default: "" },
    aspectRatio: { type: String, default: undefined },
    rounded: { type: String as PropType<RoundedKey>, default: "none" },
    objectFit: { type: String as PropType<ObjectFitKey>, default: "cover" },
    fluidWidth: { type: Boolean, default: false },
    fallbackIcon: { type: String, default: "ti ti-photo-off" },
  },
  emits: ["onLoadError"],
  setup(props, { emit }) {
    const hasError = ref(false);

    const roundedClass = computed(() => ROUNDED_CLASS_MAP[props.rounded]);
    const aspectRatioStyle = computed(() =>
      props.aspectRatio ? { aspectRatio: props.aspectRatio } : undefined,
    );

    function onError(event: Event) {
      hasError.value = true;
      emit("onLoadError", event);
    }

    watch(
      () => props.src,
      () => {
        hasError.value = false;
      },
    );

    return { hasError, roundedClass, aspectRatioStyle, onError };
  },
});
</script>

<style lang="scss" scoped>
.custom-image.has-aspect-ratio {
  display: block;
  width: 100%;
  & .custom-image-inner {
    width: 100%;
    height: 100%;
  }
}
.custom-image-inner {
  display: block;
  width: inherit;
  height: inherit;
}
.fluid-width .custom-image-inner {
  width: 100%;
  height: auto;
}
</style>
